/**
 * The ridge, the brand's one ornament, as a texture (owner S1).
 * Ported from src/dev/reference/aesthetic.jsx §4 makeRidgeTexture (L488–527), with the
 * gradient-noise helpers it needs (clamp L301, GRAD / hash2 / fade / perlin2 / fbm2 L437–479).
 *
 * One tileable fingerprint field, rendered once to a 512 px alpha texture (white
 * ridge lines on transparent). It is exposed as `--ridge: url(data:image/png;…)`, which
 * is the value the original put inline on its .ld root. It is never shown as wallpaper
 * at full strength: paper grain masks it at about 3.4 %, and raking light reveals it
 * only under the hand (.ld-paper::before, .ld-earth::before, .ld-rake::after in
 * css/base.css).
 *
 * The empty fallback (`linear-gradient(transparent,transparent)`) lives on :root in
 * base.css, so an inline --ridge on <html> reaches every .ld below it.
 */

const clamp = (v: number, a = 0, b = 1): number => (v < a ? a : v > b ? b : v);

// Gradient noise. Hash by Math.imul, 256 gradient directions, optional period for tiling.
const GRAD = (() => {
  const g = new Float32Array(512);
  for (let i = 0; i < 256; i++) {
    const a = (i / 256) * Math.PI * 2;
    g[i * 2] = Math.cos(a);
    g[i * 2 + 1] = Math.sin(a);
  }
  return g;
})();

function hash2(x: number, y: number, s: number): number {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(s | 0, 1442695041)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);

function perlin2(x: number, y: number, seed = 0, period = 0): number {
  let x0 = Math.floor(x),
    y0 = Math.floor(y);
  const fx = x - x0,
    fy = y - y0;
  let x1 = x0 + 1,
    y1 = y0 + 1;
  if (period) {
    const w = (v: number) => ((v % period) + period) % period;
    x0 = w(x0);
    x1 = w(x1);
    y0 = w(y0);
    y1 = w(y1);
  }
  const g = (ix: number, iy: number, dx: number, dy: number) => {
    const k = (hash2(ix, iy, seed) & 255) << 1;
    return GRAD[k] * dx + GRAD[k + 1] * dy;
  };
  const u = fade(fx),
    v = fade(fy);
  const a = g(x0, y0, fx, fy),
    b = g(x1, y0, fx - 1, fy),
    c = g(x0, y1, fx, fy - 1),
    d = g(x1, y1, fx - 1, fy - 1);
  const top = a + (b - a) * u,
    bot = c + (d - c) * u;
  return top + (bot - top) * v;
}

function fbm2(x: number, y: number, oct = 4, seed = 0, period = 0): number {
  let a = 0.5,
    f = 1,
    s = 0,
    norm = 0;
  for (let i = 0; i < oct; i++) {
    s += a * perlin2(x * f, y * f, seed + i * 17, period ? period * f : 0);
    norm += a;
    a *= 0.5;
    f *= 2;
  }
  return s / norm;
}

let RIDGE_URL: string | null = null;

/** Renders the texture once and returns its PNG data URL. Returns null outside the browser or without a 2D canvas. */
function makeRidgeTexture(size = 512): string | null {
  if (RIDGE_URL || typeof document === "undefined") return RIDGE_URL;
  const N = size / 2,
    S = N + 1,
    P = 3,
    R = 29,
    A = 4.4;
  const F = new Float32Array(S * S);
  for (let j = 0; j <= N; j++) {
    for (let i = 0; i <= N; i++) {
      const u = (i / N) * P,
        v = (j / N) * P;
      const wu = 0.8 * fbm2(u + 3.1, v + 7.7, 2, 5, P),
        wv = 0.8 * fbm2(u + 9.4, v + 1.2, 2, 6, P);
      F[j * S + i] = R * (j / N) + A * fbm2(u + wu, v + wv, 3, 3, P);
    }
  }
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");
  if (!ctx) return null;
  const img = ctx.createImageData(size, size),
    d = img.data;
  for (let y = 0; y < size; y++) {
    const gy = y / 2,
      j = Math.min(N - 1, gy | 0),
      fy = gy - j;
    for (let x = 0; x < size; x++) {
      const gx = x / 2,
        i = Math.min(N - 1, gx | 0),
        fx = gx - i;
      const f00 = F[j * S + i],
        f10 = F[j * S + i + 1],
        f01 = F[(j + 1) * S + i],
        f11 = F[(j + 1) * S + i + 1];
      const top = f00 + (f10 - f00) * fx,
        bot = f01 + (f11 - f01) * fx,
        f = top + (bot - top) * fy;
      const dx = ((f10 - f00) * (1 - fy) + (f11 - f01) * fy) / 2,
        dy = (bot - top) / 2;
      const g = Math.sqrt(dx * dx + dy * dy) + 1e-6;
      const dist = Math.abs(f - Math.floor(f) - 0.5) / g;
      const o = (y * size + x) * 4;
      d[o] = d[o + 1] = d[o + 2] = 255;
      d[o + 3] = 255 * clamp(2.7 - dist + 0.5);
    }
  }
  ctx.putImageData(img, 0, 0);
  RIDGE_URL = c.toDataURL("image/png");
  return RIDGE_URL;
}

const applied = new WeakSet<HTMLElement>();

/**
 * Renders the ridge texture on the first call, then sets
 * `--ridge: url(data:image/png;base64,…)` inline on `target` (default: <html>).
 * Safe to call any number of times, from any component. The texture is rendered
 * once per page, and a target that already has it is left alone.
 * The first call does the work synchronously: about 150 ms on a headless software
 * CPU, which is why it belongs behind the loader. Every later call costs nothing.
 * Returns the data URL, or null when there is no DOM (SSR or tests without a canvas).
 */
export function ensureRidge(target?: HTMLElement | null): string | null {
  if (typeof document === "undefined") return null;
  const el = target ?? document.documentElement;
  const url = makeRidgeTexture();
  if (!url) return null;
  // The WeakSet keeps repeat calls O(1). Reading the inline value back would copy the ~210 KB data URL each time.
  if (!applied.has(el)) {
    el.style.setProperty("--ridge", `url(${url})`);
    applied.add(el);
  }
  return url;
}

/** The rendered texture's data URL, or null if ensureRidge() has not run yet. */
export function ridgeUrl(): string | null {
  return RIDGE_URL;
}
